import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';
import { BlogPost } from '../components/blog/BlogPost';
import { BlogSidebar } from '../components/blog/BlogSidebar';
import { BlogCategories } from '../components/blog/BlogCategories';
import { SearchBar } from '../components/blog/SearchBar';
import { useBlogStore } from '../store/blogStore';
import { WalletBox } from '../components/WalletBox';

export const BlogPage: React.FC = () => {
  const { posts, loading, error, fetchPosts, categories } = useBlogStore();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const filteredPosts = posts.filter(post => {
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-neon-green animate-pulse">Loading posts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-red-500 mb-4">Failed to load posts</div>
        <button
          onClick={() => fetchPosts()}
          className="px-4 py-2 bg-neon-green/20 text-neon-green rounded-lg hover:bg-neon-green/30"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div 
        className="glass p-8 relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="relative flex items-center justify-between">
          <div>
            <motion.h1 
              className="text-6xl font-bold bg-gradient-to-r from-neon-green via-neon-purple to-neon-green bg-clip-text text-transparent mb-4"
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] 
              }}
              transition={{ 
                duration: 5,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{ backgroundSize: '200% auto' }}
            >
              L I F E T R A C K  B L O G
            </motion.h1>
            <div className="text-xl text-gray-400 flex items-center space-x-3">
              <Zap className="w-5 h-5 text-neon-green" />
              <span>Insights. Knowledge. Growth.</span>
            </div>
          </div>
          <WalletBox balance={300000} earned={15000} />
        </div>
      </motion.div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-8 space-y-6">
          <div className="glass p-4">
            <SearchBar 
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>

          <BlogCategories 
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />

          {filteredPosts.length > 0 ? (
            <div className="space-y-6">
              {filteredPosts.map(post => (
                <BlogPost key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              No posts found
            </div>
          )}
        </div>

        <div className="col-span-4">
          <BlogSidebar />
        </div>
      </div>
    </div>
  );
};